import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, FileText, Loader, Search, Sparkles } from 'lucide-react';
import { useDocketAutocomplete } from '../hooks/useDocketAutocomplete';

const EXAMPLE_DOCKETS: Array<{ docket: string; title: string }> = [
  { docket: '26-54', title: 'Spectrum Abundance for Space Innovation' },
  { docket: '23-376', title: 'Robocall Mitigation' },
  { docket: '11-42', title: 'Lifeline & Link Up Modernization' },
];

export const CommentSummariesEntryPage: React.FC = () => {
  const navigate = useNavigate();
  const [docket, setDocket] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    suggestions,
    isLoading: suggestionsLoading,
    showSuggestions,
    selectedIndex,
    setQuery,
    selectSuggestion,
    handleKeyDown,
    setShowSuggestions,
  } = useDocketAutocomplete({ minQueryLength: 2, maxSuggestions: 8 });

  const navigateToDocket = useCallback(
    (raw: string) => {
      const trimmed = raw.trim();
      if (!trimmed) return;
      navigate(`/comment-summaries/${encodeURIComponent(trimmed)}`);
    },
    [navigate]
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      navigateToDocket(docket);
    },
    [docket, navigateToDocket]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setDocket(v);
    setQuery(v);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const picked = handleKeyDown(e);
    if (picked) {
      setDocket(picked);
      navigateToDocket(picked);
    }
  };

  const handleSuggestionClick = (idx: number) => {
    const picked = selectSuggestion(idx);
    if (picked) {
      setDocket(picked);
      navigateToDocket(picked);
    }
  };

  // Close dropdown when clicking outside the search area.
  useEffect(() => {
    const onClick = (ev: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(ev.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [setShowSuggestions]);

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-10 flex flex-col items-center text-center">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-cg-green/10 text-cg-green">
          <Sparkles className="h-6 w-6" />
        </div>
        <h1 className="font-display text-4xl font-normal tracking-tight text-cg-ink">
          Comment Summaries
        </h1>
        <p className="mt-3 max-w-xl text-base text-neutral-600">
          Turn every comment on an FCC docket into one shareable summary — page-cited,
          topic-tagged, and ready to send in under three minutes.
        </p>
      </div>

      <div ref={containerRef} className="relative mx-auto mb-6 w-full max-w-xl">
        <form
          onSubmit={handleSubmit}
          className="flex items-center gap-2 rounded-xl border border-cg-border bg-white p-2 shadow-sm focus-within:border-cg-green focus-within:ring-2 focus-within:ring-cg-green/20"
        >
          <Search className="ml-2 h-5 w-5 shrink-0 text-neutral-400" />
          <input
            autoFocus
            type="text"
            value={docket}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onFocus={() => {
              if (suggestions.length > 0) setShowSuggestions(true);
            }}
            placeholder="Enter a docket number (e.g. 26-54)"
            className="flex-1 bg-transparent px-1 py-2 text-base text-cg-ink placeholder:text-neutral-400 focus:outline-none"
            autoComplete="off"
          />
          {suggestionsLoading && (
            <Loader className="mr-1 h-4 w-4 shrink-0 animate-spin text-neutral-400" />
          )}
          <button
            type="submit"
            disabled={!docket.trim()}
            className="flex shrink-0 items-center gap-1.5 rounded-lg bg-cg-nav px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-cg-green disabled:cursor-not-allowed disabled:opacity-50"
          >
            Summarize
            <ArrowRight className="h-4 w-4" />
          </button>
        </form>

        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-xl border border-cg-border bg-white shadow-lg">
            {suggestions.map((s, idx) => (
              <button
                key={`${s.docketNumber}-${idx}`}
                type="button"
                onClick={() => handleSuggestionClick(idx)}
                onMouseDown={(e) => e.preventDefault()}
                className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                  idx === selectedIndex ? 'bg-cg-green/5 text-cg-ink' : 'text-neutral-700 hover:bg-neutral-50'
                }`}
              >
                <FileText className="h-4 w-4 shrink-0 text-neutral-400" />
                <div className="min-w-0">
                  <div className="font-medium text-cg-ink">{s.docketNumber}</div>
                  {s.displayText && s.displayText !== s.docketNumber && (
                    <div className="truncate text-xs text-neutral-500">{s.displayText}</div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mx-auto mb-10 max-w-xl">
        <p className="mb-3 text-xs font-medium uppercase tracking-wider text-neutral-500">
          Try a popular docket
        </p>
        <div className="space-y-1.5">
          {EXAMPLE_DOCKETS.map((ex) => (
            <Link
              key={ex.docket}
              to={`/comment-summaries/${ex.docket}`}
              className="group flex items-center justify-between rounded-lg border border-cg-border bg-white px-4 py-3 transition-colors hover:border-cg-green hover:bg-cg-green/5"
            >
              <div className="flex items-center gap-3">
                <FileText className="h-4 w-4 text-neutral-400 group-hover:text-cg-green" />
                <div>
                  <div className="text-sm font-semibold text-cg-ink">{ex.docket}</div>
                  <div className="text-xs text-neutral-500">{ex.title}</div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-neutral-400 transition-transform group-hover:translate-x-0.5 group-hover:text-cg-green" />
            </Link>
          ))}
        </div>
      </div>

      <p className="text-center text-sm text-neutral-500">
        Want to browse first?{' '}
        <Link to="/proceedings" className="font-medium text-cg-green hover:underline">
          See all dockets →
        </Link>
      </p>
    </div>
  );
};
